'use client';

import { HomeDataVilla } from '@/actions/home';
import Bath from '@/components/Icons/Bath';
import Bed from '@/components/Icons/Bed';
import EmptyStarIcon from '@/components/Icons/EmptyStar';
import MapPinIcon from '@/components/Icons/MapPin';
import { WishList } from '@/components/VillageCard/Options';
import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import { ICurrencyDictonary } from '@/dictionaries/utils/currency/types';
import { useCurrency } from '@/contexts/currencyContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import { cn } from '@/lib/utils';
import { VillageFeature } from '@/services/types';
import MediaFilterImage from '@/components/Media/MediaFilterImage';
import { getMediaFilterSrc } from '@/utils/get-media-filter';
import { sicilyMap, villagesMap } from '@/utils/maps';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import guests from '../../../../public/guests.png';

type Props = {
  villa: HomeDataVilla;
  dict: IRegionZoneDictonary;
  currencyDict: ICurrencyDictonary;
  lang: Locale;
  isFirst?: boolean;
};

export default function VillaCard({
  villa,
  dict,
  currencyDict,
  lang,
  isFirst = false,
}: Props) {
  const { currency, applyCurrency } = useCurrency();

  const coverImage = villa.villaMedia[0];

  const villageSubtitle = `${villa.city} (${dict[villa.zone.code]})`;

  function getVillageLink() {
    let baseUrl = '';

    if (lang !== 'en') {
      baseUrl = `/${lang}`;
    }

    return `${baseUrl}/${sicilyMap[lang]}/${villagesMap[lang]}/${villa.slug}`;
  }

  const price = new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: currency.value,
    maximumFractionDigits: 0,
  }).format(
    applyCurrency(Math.floor(Number(villa.villaPrice[0]?.originalPrice) || 0)),
  );

  return (
    <Link
      target="_blank"
      className="bg-white hover:shadow-default transition-shadow inline-block outline-none overflow-hidden relative w-full cursor-pointer z-10"
      href={getVillageLink()}>
      <div
        className={cn(
          'w-full relative overflow-hidden',
          isFirst ? 'h-[680px]' : '',
        )}>
        <div
          className={cn(
            'w-full bg-cover bg-[50%]',
            isFirst ? 'absolute bottom-0 left-0  h-[680px]' : 'aspect-square',
          )}>
          <MediaFilterImage
            media={coverImage?.media}
            filterName="villa_card_2x_webp"
            alt={villa.title}
            fill
            className="block w-full h-full object-cover align-middle max-w-full"
            sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
            style={{ objectFit: 'cover' }}
          />
        </div>

        <WishlistButton villa={villa} isFirst={isFirst} />
      </div>
      <div
        className={cn(
          'text-black flex flex-col gap-2',
          isFirst
            ? 'absolute text-white bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent flex-col tablet:flex-row justify-between items-start tablet:items-end p-6'
            : 'bg-white px-4 py-3',
        )}>
        <div className={cn('flex flex-col', isFirst ? 'gap-4' : 'gap-5 mb-1')}>
          <div className="flex flex-col">
            <div className="text-left float-left">
              <h3
                className={cn(
                  'leading-[1.25] font-title tracking-normal',
                  isFirst ? 'text-4.5xl mb-3' : 'text-2xl mb-2 text-gray-900',
                )}>
                {villa.title}
              </h3>
              <div>
                <span className="text-sm flex gap-0.5 items-center">
                  <MapPinIcon
                    className={cn(
                      'size-5',
                      isFirst ? 'text-gray-500' : 'text-gray-800',
                    )}
                  />
                  <span
                    className={cn(
                      'flex-1',
                      isFirst
                        ? 'text-base font-averta-light'
                        : 'text-smfont-averta text-gray-900',
                    )}>
                    {villageSubtitle}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <ul className="list-none leading-[1.6] flex gap-4">
            <li className="flex self-end items-center gap-1">
              <Image
                width={isFirst ? 20 : 16}
                height={isFirst ? 20 : 16}
                src={guests}
                alt="Guests icon image"
              />
              <span className={cn(isFirst ? 'text-base' : 'text-sm')}>
                {villa.sleeps}
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Bath className={cn(isFirst ? 'size-5' : 'size-4')} />
              <span className={cn(isFirst ? 'text-base' : 'text-sm')}>
                {villa.bedrooms}
              </span>
            </li>
            <li className="flex items-center gap-2">
              <Bed className={cn(isFirst ? 'size-5' : 'size-4')} />
              <span className={cn(isFirst ? 'text-base' : 'text-sm')}>
                {villa.bathrooms}
              </span>
            </li>
          </ul>
        </div>
        {isFirst ? null : <div className="w-20 border-b"></div>}
        <div
          className={cn(
            'font-noe-regular font-normal tracking-wide',
            isFirst ? 'text-lg' : 'text-base',
          )}>
          {currencyDict.from}
          <span
            className={cn(
              'font-noe tracking-wider mx-2',
              isFirst ? 'text-3xl' : 'text-2xl ',
            )}>
            {price}
          </span>
          /{currencyDict.perWeek}
        </div>
      </div>
    </Link>
  );
}

function WishlistButton({
  villa,
  isFirst,
}: {
  villa: HomeDataVilla;
  isFirst?: boolean;
}) {
  const [wishList, setWishList] = useLocalStorage('villas', [] as WishList[]);
  const [inWishList, setInWishList] = useState(false);

  useEffect(() => {
    const isSaved = wishList.find(
      (savedVillage) => savedVillage.title === villa.title,
    );
    if (isSaved) {
      setInWishList(true);
    } else {
      setInWishList(false);
    }
    //eslint-disable-next-line
  }, [wishList]);

  const villageMainFeatures: VillageFeature[] = villa.villaFeature
    ?.filter(({ feature }) => {
      return feature?.type ? feature.type === 'main_feature' : false;
    })
    .map(({ feature: { featureTranslation, ...feature }, ...vf }) => ({
      ...vf,
      feature: { ...feature, featureTranslation, priority: vf.priority },
      translations: [],
    }));

  const { currency } = useCurrency();

  const cover = villa.villaMedia[0];

  const villageMedia =
    getMediaFilterSrc({
      media: cover?.media,
      filterName: ['medium_3-2', 'villa_card_2x_webp'],
    }) ?? '';
  const coverMedia = cover
    ? {
        id: cover.media.id,
        path: cover.media.path,
        filename: cover.media.filename,
        mediaFilters: cover.media.mediaFilters,
      }
    : undefined;

  const wishListInfos: WishList = {
    id: villa.id,
    title: villa.title,
    sleeps: villa.sleeps,
    bedrooms: villa.bedrooms,
    bathrooms: villa.bathrooms,
    city: villa.city,
    media: villageMedia ? villageMedia : '',
    coverMedia,
    prices: {
      originalPrice: villa.villaPrice[0]?.originalPrice.toString(),
      ownerPrice: villa.villaPrice[0]?.originalPrice.toString(), // should be owner_price, but this is consistent with existing wishlist saving data
      currency: currency.value,
    },
    zone: {
      code: villa.zone.code,
      enabled: true,
    },
    villageMainFeatures,
    slug: villa.slug,
  };

  function handleVillageWishList(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    let newWishList: WishList[] = [];

    if (!inWishList) {
      newWishList = [...wishList, wishListInfos];
    } else {
      newWishList = wishList.filter(
        (saved) => saved.title !== wishListInfos.title,
      );
    }
    setWishList(newWishList);
  }

  return (
    <button
      id={`${villa.slug}-favorite`}
      aria-label={`Favorite ${villa.title} village`}
      tabIndex={-1}
      onClick={handleVillageWishList}
      className={cn(
        'absolute cursor-pointer appearance-none bg-transparent border-0 z-10',
        isFirst ? 'top-4 right-4' : 'top-2 right-2',
      )}>
      {inWishList ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 h-8 group-[.is-absolute]:h-6 group-[.is-absolute]:w-6 fill-current text-white"
          viewBox="0 0 24 24">
          <path fill="none" d="M0 0h24v24H0z" data-name="Rectangle 4" />
          <path
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeWidth="1.5"
            d="m12 18-5.878 3.09 1.123-6.545L2.489 9.91l6.572-.955L12 3l2.939 5.955 6.572.955-4.755 4.635 1.123 6.545Z"
          />
        </svg>
      ) : (
        <EmptyStarIcon className="w-8 h-[32px] fill-current text-white" />
      )}
    </button>
  );
}

Intl.NumberFormat;
